import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from "react";
import {
  fetchMonthlyData,
  extractMonthlyDataFromLifetime,
  fetchLifetimeThreadsOptimized,
  fetchLifetimeInsightsPure,
  computeWeeklyEngagement,
  fetchAllRepliesPure,
  fetchAllConversationsPure,
  computeRepliesByUser,
  analyzeUserInteractions,
  computeBoxPlotData,
  fetchRecentConversationsForTest,
  getMyCollections,
  updateRecentThreadsInsights,
  updateThreadsInFirestore,
} from "../utils/pureDataFetch";
import { fetchFriendProfilePictures } from "../utils/friendProfileFetch";
import moment from "moment";
import i18n from "../i18n/config";
import { dbService as db } from "../firebaseInstance";

// 안정적인 빈 배열 참조
const EMPTY_ARRAY = [];

// 초기 상태
const initialState = {
  // === Timeline Data (Dashboard 전용) ===
  timeline: {
    months: [], // Array<{ key, month, year, threads, insight, isLoaded, source, loadedAt }>
    hasMoreFromAPI: true,
    hasMoreFromLifetime: true,
    isLoadingMore: false,
    phase: "initial", // 'initial' | 'api-loading' | 'lifetime-ready' | 'complete'
  },

  // === Lifetime Data (단순화) ===
  lifetimeThreadsList: [],
  lifetimeInsight: null,
  lifetimeWeeklyEngagement: [],
  lifetimeReplies: [],
  repliesByUser: [],
  boxPlotData: {},

  // === 점진적 로딩 상태 ===
  progressiveLoading: {
    stage: 'initial', // 'initial' | 'connecting' | 'loading-range' | 'page-loaded' | 'saving' | 'complete'
    dateRange: {
      start: null,
      end: null
    },
    pageCount: 0,
    threadCount: 0,
    lastSavedDate: null,
    isSaving: false,
    progress: 0,
    error: null
  },

  // === Friends 점진적 로딩 상태 ===
  friendsLoadingProgress: {
    completed: 0,
    total: 0,
    usernames: [],       // 발견된 유저네임 목록
    conversations: [],   // 지금까지 로드된 대화들
  },

  // === UI 표시용 (성능 최적화) ===
  visibleThreads: [],
  displayLimit: 20, // 초기 표시 개수

  // === 섹션별 데이터 모델 (새로 추가) ===
  sectionDataModels: {
    // 1. 상위 성과 스레드 섹션
    topPerformanceThreads: {
      threads: [],
      isReady: false,
      error: null,
      lastUpdated: null,
    },

    // 2. 성과 요약 섹션
    performanceSummary: {
      profile: null,
      metrics: {
        followers: 0,
        likes: 0,
        replies: 0,
        reposts: 0,
        numThreads: 0,
        profileViews: [],
      },
      isReady: false,
      error: null,
      lastUpdated: null,
    },

    // 3. 스레드 성능 개요 섹션
    threadPerformanceOverview: {
      threads: [],
      boxPlotData: {},
      isReady: false,
      error: null,
      lastUpdated: null,
    },

    // 4. 주요 지표 차트 섹션
    keyMetricChart: {
      insights: {},
      weeklyEngagement: [],
      isReady: false,
      error: null,
      lastUpdated: null,
    },

    // 5. 친구 상호작용 섹션
    friendsInteraction: {
      repliesByUser: [],
      lifetimeReplies: [],
      isReady: false,
      error: null,
      lastUpdated: null,
    },

    // 6. 컬렉션 섹션
    collections: {
      myCollections: [],
      isReady: false,
      isLoading: false,
      error: null,
      lastUpdated: null,
    },
  },

  // === 통합 로딩 상태 ===
  isLifetimeLoading: false, // 전체 라이프타임 데이터 로딩 중
  isLifetimeReady: false, // 모든 라이프타임 데이터 준비 완료
  error: null,

  // Timeline 전용 로딩 상태
  loadingStates: {
    timelineInitial: false,
    timelineMore: false,
  },

  // === 진행률 상태 (Progress Feedback) ===
  loadingProgress: {
    stage: null,
    message: null,
    subMessage: null,
    completed: 0,
    total: 0,
  },

  // === Firestore 1MB 문서 분할 관리 ===
  shardingStatus: {
    isApproachingLimit: false,
    nextTargetDocument: "threads",
    currentDocumentSize: 0,
    documentSizes: {},
    lastUpdatedAt: null,
  },
};

// 액션 타입 정의
export const ThreadsTypes = {
  // === Lifetime Actions (단순화) ===
  SET_LIFETIME_LOADING: "SET_LIFETIME_LOADING",
  SET_LIFETIME_READY: "SET_LIFETIME_READY",
  SET_THREADS: "SET_THREADS",
  SET_INSIGHT: "SET_INSIGHT",
  SET_WEEKLY_ENGAGEMENT: "SET_WEEKLY_ENGAGEMENT",
  SET_LIFETIME_REPLIES: "SET_LIFETIME_REPLIES",
  SET_FRIENDS_LOADING_PROGRESS: "SET_FRIENDS_LOADING_PROGRESS",
  SET_COMPUTED_DATA: "SET_COMPUTED_DATA",
  SET_ERROR: "SET_ERROR",
  CLEAR_DATA: "CLEAR_DATA",
  UPDATE_THREAD_METRICS: "UPDATE_THREAD_METRICS",

  // === 섹션별 데이터 모델 Actions (새로 추가) ===
  SET_SECTION_DATA: "SET_SECTION_DATA",
  SET_SECTION_READY: "SET_SECTION_READY",
  SET_SECTION_ERROR: "SET_SECTION_ERROR",
  RESET_SECTION_DATA: "RESET_SECTION_DATA",

  // === Timeline Actions ===
  SET_TIMELINE_PHASE: "SET_TIMELINE_PHASE",
  ADD_TIMELINE_MONTH: "ADD_TIMELINE_MONTH",
  UPDATE_TIMELINE_FLAGS: "UPDATE_TIMELINE_FLAGS",
  SET_TIMELINE_LOADING: "SET_TIMELINE_LOADING",

  // === Progress Actions ===
  SET_LOADING_STATE: "SET_LOADING_STATE",

  // === 점진적 로딩 Actions ===
  APPEND_THREADS_PAGE: "APPEND_THREADS_PAGE",
  UPDATE_LOADING_PROGRESS: "UPDATE_LOADING_PROGRESS", 
  UPDATE_VISIBLE_THREADS: "UPDATE_VISIBLE_THREADS",
  RESET_LOADING_STATE: "RESET_LOADING_STATE",

  // === Firestore Sharding Actions ===
  UPDATE_SHARDING_STATUS: "UPDATE_SHARDING_STATUS",
  SET_SHARDING_METADATA: "SET_SHARDING_METADATA",
  RESET_SHARDING_STATUS: "RESET_SHARDING_STATUS",
};

// === 섹션별 데이터 모델 생성 함수들 ===

const createTopPerformanceThreadsModel = (threads) => ({
  threads: Array.isArray(threads)
    ? threads.sort((a, b) => b.views - a.views)
    : [],
  isReady: true,
  error: null,
  lastUpdated: new Date().toISOString(),
});

const createPerformanceSummaryModel = (insight, profile) => ({
  profile: profile || null,
  metrics: {
    followers: insight?.followers || 0,
    likes: insight?.likes || 0,
    replies: insight?.replies || 0,
    reposts: insight?.reposts || 0,
    numThreads: insight?.numThreads || 0,
    profileViews: insight?.profileViews || [],
  },
  isReady: true,
  error: null,
  lastUpdated: new Date().toISOString(),
});

const createThreadPerformanceOverviewModel = (threads, boxPlotData) => ({
  threads: Array.isArray(threads) ? threads : [],
  boxPlotData: boxPlotData || {},
  isReady: true,
  error: null,
  lastUpdated: new Date().toISOString(),
});

const createKeyMetricChartModel = (insights, weeklyEngagement) => ({
  insights: insights || {},
  weeklyEngagement: Array.isArray(weeklyEngagement) ? weeklyEngagement : [],
  isReady: true,
  error: null,
  lastUpdated: new Date().toISOString(),
});

const createFriendsInteractionModel = (repliesByUser, lifetimeReplies) => ({
  repliesByUser: Array.isArray(repliesByUser) ? repliesByUser : [],
  lifetimeReplies: Array.isArray(lifetimeReplies) ? lifetimeReplies : [],
  isReady: true,
  error: null,
  lastUpdated: new Date().toISOString(),
});

const createCollectionsModel = (myCollections) => ({
  myCollections: Array.isArray(myCollections) ? myCollections : [],
  isReady: true,
  isLoading: false,
  error: null,
  lastUpdated: new Date().toISOString(),
});

// 리듀서 정의
function threadsReducer(state, action) {
  switch (action.type) {
    case ThreadsTypes.SET_LIFETIME_LOADING:
      return {
        ...state,
        isLifetimeLoading: action.payload,
        isLifetimeReady: action.payload ? false : state.isLifetimeReady,
      };
    case ThreadsTypes.SET_LIFETIME_READY:
      return {
        ...state,
        isLifetimeReady: action.payload,
        isLifetimeLoading: action.payload ? false : state.isLifetimeLoading,
      };
    case ThreadsTypes.SET_THREADS:
      return {
        ...state,
        lifetimeThreadsList: action.payload,
      };
    case ThreadsTypes.SET_INSIGHT:
      return {
        ...state,
        lifetimeInsight: action.payload,
      };
    case ThreadsTypes.SET_WEEKLY_ENGAGEMENT:
      return {
        ...state,
        lifetimeWeeklyEngagement: action.payload,
      };
    case ThreadsTypes.SET_LIFETIME_REPLIES:
      return {
        ...state,
        lifetimeReplies: action.payload,
      };
    case ThreadsTypes.SET_FRIENDS_LOADING_PROGRESS:
      return {
        ...state,
        friendsLoadingProgress: {
          ...state.friendsLoadingProgress,
          ...action.payload,
        },
      };
    case ThreadsTypes.SET_COMPUTED_DATA:
      return {
        ...state,
        repliesByUser: action.payload.repliesByUser || state.repliesByUser,
        boxPlotData: action.payload.boxPlotData || state.boxPlotData,
      };
    case ThreadsTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLifetimeLoading: false,
        isLifetimeReady: false,
      };
    case ThreadsTypes.CLEAR_DATA:
      return initialState;
    case ThreadsTypes.UPDATE_THREAD_METRICS:
      const { threadId, metrics } = action.payload;
      return {
        ...state,
        lifetimeThreadsList: state.lifetimeThreadsList.map((thread) =>
          thread.id === threadId ? { ...thread, ...metrics } : thread
        ),
      };

    // === 섹션별 데이터 모델 관리 ===
    case ThreadsTypes.SET_SECTION_DATA:
      const { section, data } = action.payload;
      return {
        ...state,
        sectionDataModels: {
          ...state.sectionDataModels,
          [section]: {
            ...state.sectionDataModels[section],
            ...data,
          },
        },
      };

    case ThreadsTypes.SET_SECTION_READY:
      return {
        ...state,
        sectionDataModels: {
          ...state.sectionDataModels,
          [action.payload.section]: {
            ...state.sectionDataModels[action.payload.section],
            isReady: action.payload.isReady,
            lastUpdated: new Date().toISOString(),
          },
        },
      };

    case ThreadsTypes.SET_SECTION_ERROR:
      return {
        ...state,
        sectionDataModels: {
          ...state.sectionDataModels,
          [action.payload.section]: {
            ...state.sectionDataModels[action.payload.section],
            error: action.payload.error,
            isReady: false,
            lastUpdated: new Date().toISOString(),
          },
        },
      };

    case ThreadsTypes.RESET_SECTION_DATA:
      const sectionToReset = action.payload.section;
      return {
        ...state,
        sectionDataModels: {
          ...state.sectionDataModels,
          [sectionToReset]: {
            ...initialState.sectionDataModels[sectionToReset],
          },
        },
      };

    case ThreadsTypes.SET_TIMELINE_PHASE:
      return {
        ...state,
        timeline: {
          ...state.timeline,
          phase: action.payload,
        },
      };

    case ThreadsTypes.ADD_TIMELINE_MONTH:
      const { monthData, source } = action.payload;
      const existingIndex = state.timeline.months.findIndex(
        (m) => m.month === monthData.month && m.year === monthData.year
      );

      if (existingIndex >= 0) {
        // 기존 월 데이터 업데이트
        const updatedMonths = [...state.timeline.months];
        updatedMonths[existingIndex] = {
          ...updatedMonths[existingIndex],
          ...monthData,
          source,
          isLoaded: true,
          loadedAt: new Date().toISOString(),
        };
        return {
          ...state,
          timeline: {
            ...state.timeline,
            months: updatedMonths,
          },
        };
      } else {
        // 새 월 데이터 추가
        return {
          ...state,
          timeline: {
            ...state.timeline,
            months: [
              ...state.timeline.months,
              {
                key: `${monthData.year}-${monthData.month}`,
                month: monthData.month,
                year: monthData.year,
                threads: monthData.threads,
                insight: monthData.insight,
                isLoaded: true,
                source,
                loadedAt: new Date().toISOString(),
              },
            ].sort((a, b) => {
              // 최신 월부터 정렬
              if (a.year !== b.year) return b.year - a.year;
              return b.month - a.month;
            }),
          },
        };
      }

    case ThreadsTypes.UPDATE_TIMELINE_FLAGS:
      return {
        ...state,
        timeline: {
          ...state.timeline,
          hasMoreFromAPI:
            action.payload.hasMoreFromAPI ?? state.timeline.hasMoreFromAPI,
          hasMoreFromLifetime:
            action.payload.hasMoreFromLifetime ??
            state.timeline.hasMoreFromLifetime,
        },
      };

    case ThreadsTypes.SET_TIMELINE_LOADING:
      return {
        ...state,
        timeline: {
          ...state.timeline,
          isLoadingMore: action.payload,
        },
        loadingStates: {
          ...state.loadingStates,
          timelineMore: action.payload,
        },
      };

    case ThreadsTypes.SET_LOADING_STATE:
      return {
        ...state,
        loadingProgress: {
          stage: action.payload.progress?.stage || null,
          message: action.payload.progress?.message || null,
          subMessage: action.payload.progress?.subMessage || null,
          completed: action.payload.progress?.completed || 0,
          total: action.payload.progress?.total || 0,
        },
      };

    // === Firestore Sharding Reducer Cases ===
    case ThreadsTypes.UPDATE_SHARDING_STATUS:
      return {
        ...state,
        shardingStatus: {
          ...state.shardingStatus,
          ...action.payload,
        },
      };

    case ThreadsTypes.SET_SHARDING_METADATA:
      return {
        ...state,
        shardingStatus: action.payload,
      };

    case ThreadsTypes.RESET_SHARDING_STATUS:
      return {
        ...state,
        shardingStatus: {
          isApproachingLimit: false,
          nextTargetDocument: "threads",
                currentDocumentSize: 0,
          documentSizes: {},
          lastUpdatedAt: null,
        },
      };

    // === 점진적 로딩 케이스들 ===
    case ThreadsTypes.APPEND_THREADS_PAGE:
      // ✅ 개선: ID 기반 중복 제거로 실시간 업데이트 중 중복 표시 방지
      const existingIds = new Set(state.lifetimeThreadsList.map(t => t.id));
      const uniqueNewThreads = action.payload.threads.filter(t => !existingIds.has(t.id));
      const newThreads = [...state.lifetimeThreadsList, ...uniqueNewThreads];
      
      console.log(`🔍 점진적 로딩 중복 체크:`, {
        기존: state.lifetimeThreadsList.length,
        신규: action.payload.threads.length,
        중복제거후: uniqueNewThreads.length,
        최종: newThreads.length
      });
      
      return {
        ...state,
        lifetimeThreadsList: newThreads,
        progressiveLoading: {
          ...state.progressiveLoading,
          ...action.payload,
          threadCount: newThreads.length
        },
        visibleThreads: newThreads.slice(0, state.displayLimit),
        isLifetimeLoading: true
      };
      
    case ThreadsTypes.UPDATE_LOADING_PROGRESS:
      return {
        ...state,
        progressiveLoading: {
          ...state.progressiveLoading,
          ...action.payload
        }
      };
      
    case ThreadsTypes.UPDATE_VISIBLE_THREADS:
      return {
        ...state,
        visibleThreads: state.lifetimeThreadsList.slice(0, action.payload.limit),
        displayLimit: action.payload.limit
      };
      
    case ThreadsTypes.RESET_LOADING_STATE:
      return {
        ...state,
        progressiveLoading: initialState.progressiveLoading,
        isLifetimeLoading: false
      };

    default:
      return state;
  }
}

// Context 생성
const ThreadsStateContext = createContext();
const ThreadsDispatchContext = createContext();

// Provider 컴포넌트
export function ThreadsProvider({ children }) {
  const [state, dispatch] = useReducer(threadsReducer, initialState);

  // Context 값 제공
  return (
    <ThreadsStateContext.Provider value={state}>
      <ThreadsDispatchContext.Provider value={dispatch}>
        {children}
      </ThreadsDispatchContext.Provider>
    </ThreadsStateContext.Provider>
  );
}

// 커스텀 훅
export function useThreadsState() {
  const context = useContext(ThreadsStateContext);
  if (context === undefined) {
    throw new Error("useThreadsState must be used within a ThreadsProvider");
  }
  return context;
}

/**
 * 최근 스레드 업데이트 기능을 포함한 커스텀 훅
 */
export function useRecentThreadsUpdate() {
  const state = useThreadsState();
  const dispatch = useThreadsDispatch();
  
  const updateRecentInsights = useCallback(async (userId) => {
    if (!userId || !state.lifetimeThreadsList || state.lifetimeThreadsList.length === 0) {
      console.warn('Cannot update recent threads: missing userId or no threads data');
      return { count: 0, message: 'No data to update' };
    }
    
    try {
      const result = await updateRecentThreadsData(userId, state.lifetimeThreadsList, dispatch);
      return result;
    } catch (error) {
      console.error('Failed to update recent threads:', error);
      throw error;
    }
  }, [state.lifetimeThreadsList, dispatch]);
  
  return {
    updateRecentInsights,
    hasRecentThreads: state.lifetimeThreadsList ? 
      state.lifetimeThreadsList.some(thread => {
        const oneWeekAgo = moment().subtract(7, 'days').unix();
        const threadTimestamp = moment(thread.timestamp).unix();
        return threadTimestamp >= oneWeekAgo;
      }) : false
  };
}

export function useThreadsDispatch() {
  const context = useContext(ThreadsDispatchContext);
  if (context === undefined) {
    throw new Error("useThreadsDispatch must be used within a ThreadsProvider");
  }
  return context;
}

// === Collections 관련 Custom Hooks ===

/**
 * 컬렉션 데이터 로딩 함수
 */
// 중복 호출 방지를 위한 로딩 상태 관리
const collectionsLoadingMap = new Map();

export async function loadCollectionsData(dispatch, userId) {
  console.log("[loadCollectionsData] START - userId:", userId);
  try {
    // 이미 로딩 중이면 기존 Promise 반환
    if (collectionsLoadingMap.has(userId)) {
      console.log("[loadCollectionsData] Already loading, returning existing promise");
      return collectionsLoadingMap.get(userId);
    }

    const loadPromise = (async () => {
      dispatch({
        type: ThreadsTypes.SET_SECTION_DATA,
        payload: {
          section: "collections",
          data: { isReady: false, isLoading: true, error: null },
        },
      });

      try {
        console.log("[loadCollectionsData] Calling getMyCollections...");
        const collections = await getMyCollections(userId);
        console.log("[loadCollectionsData] Got collections:", collections?.length || 0);

        const collectionsModel = createCollectionsModel(collections);

        dispatch({
          type: ThreadsTypes.SET_SECTION_DATA,
          payload: { section: "collections", data: collectionsModel },
        });

        return collections;
      } catch (innerError) {
        console.error("[loadCollectionsData] Error in getMyCollections:", innerError);
        
        // 상세한 에러 정보 추출
        let errorMessage = innerError.message;
        if (innerError.code === "permission-denied") {
          errorMessage = "Permission denied: Check Firestore security rules";
        } else if (innerError.code === "unavailable") {
          errorMessage = "Firestore unavailable: Check network connection";
        } else if (innerError.message?.includes("Firebase")) {
          errorMessage = `Firebase error: ${innerError.message}`;
        }
        
        dispatch({
          type: ThreadsTypes.SET_SECTION_ERROR,
          payload: { 
            section: "collections", 
            error: errorMessage 
          },
        });
        
        throw innerError;
      }
    })();

    // Promise를 Map에 저장
    collectionsLoadingMap.set(userId, loadPromise);

    // 완료되면 Map에서 제거
    loadPromise.finally(() => {
      collectionsLoadingMap.delete(userId);
    });

    return loadPromise;
  } catch (error) {
    console.error("❌ [loadCollectionsData] Outer error:", error);
    dispatch({
      type: ThreadsTypes.SET_SECTION_ERROR,
      payload: { section: "collections", error: error.message },
    });
    throw error;
  }
}

/**
 * 컬렉션 관리 훅
 */
export function useCollections() {
  const state = useThreadsState();
  const dispatch = useThreadsDispatch();

  console.log("[useCollections] State check:", {
    hasState: !!state,
    hasSectionDataModels: !!state?.sectionDataModels,
    hasCollections: !!state?.sectionDataModels?.collections,
    collectionsData: state?.sectionDataModels?.collections
  });

  const collectionsData = state.sectionDataModels?.collections || {
    myCollections: EMPTY_ARRAY,
    isReady: false,
    isLoading: false,
    error: null,
    lastUpdated: null,
  };

  const loadCollections = useCallback(
    async (userId) => {
      console.log("[useCollections] loadCollections called with userId:", userId);
      if (!userId) {
        console.error("[useCollections] No userId provided");
        return EMPTY_ARRAY;
      }
      return await loadCollectionsData(dispatch, userId);
    },
    [dispatch]
  );

  return {
    collections: collectionsData.myCollections || EMPTY_ARRAY,
    isReady: collectionsData.isReady || false,
    isLoading: collectionsData.isLoading || false,
    error: collectionsData.error,
    lastUpdated: collectionsData.lastUpdated,
    loadCollections,
  };
}

// 단순화된 Lifetime 데이터 로딩 - 섹션별 데이터 모델 생성 로직 추가
export async function loadLifetimeDataWithTimeline(
  dispatch,
  userId,
  userProfile = null,
  isAutoUpdate = false
) {
  console.log("🔍 DEBUG: loadLifetimeDataWithTimeline 시작", { userId, userProfile });
  
  // 로딩 시작
  dispatch({ type: ThreadsTypes.SET_LIFETIME_LOADING, payload: true });

  try {
    // Step 0: 메타데이터 동기화 - Firestore 분할 상태 확인
    try {
      const userDoc = await db.collection("user").doc(userId).get();
      if (userDoc.exists) {
        const metadata = userDoc.data()?.threadsMetadata;
        
        if (metadata) {
          dispatch({ 
            type: ThreadsTypes.SET_SHARDING_METADATA, 
            payload: {
              isApproachingLimit: metadata.isApproachingLimit || false,
              nextTargetDocument: metadata.nextTargetDocument || "threads",
              currentDocumentSize: metadata.currentDocumentSize || 0,
              documentSizes: metadata.documentSizes || {},
              lastUpdatedAt: metadata.lastUpdatedAt || null,
            }
          });
          
          console.log("📊 Sharding metadata synchronized:", {
            nextTarget: metadata.nextTargetDocument,
            isApproaching: metadata.isApproachingLimit
          });
        } else {
          // 메타데이터가 없으면 기본값으로 초기화
          dispatch({ 
            type: ThreadsTypes.SET_SHARDING_METADATA, 
            payload: {
              isApproachingLimit: false,
              nextTargetDocument: "threads",
              currentDocumentSize: 0,
              documentSizes: {},
              lastUpdatedAt: null,
            }
          });
          
          console.log("📊 Initialized default sharding metadata");
        }
      }
    } catch (metadataError) {
      console.warn("⚠️ Failed to sync sharding metadata (non-critical):", metadataError);
      // 메타데이터 동기화 실패해도 계속 진행
    }

    // Step 1: 스레드 데이터 가져오기 (캐시 우선) + 즉시 모델 생성
    console.log("🔍 DEBUG: fetchLifetimeThreadsOptimized 호출 시작", { isAutoUpdate });
    let allThreads = await fetchLifetimeThreadsOptimized(
      userId,
      (progress) => {
        // 1. 기존 로딩 상태 업데이트 (하위 호환성)
        dispatch({
          type: ThreadsTypes.SET_LOADING_STATE,
          payload: {
            type: "lifetime",
            isLoading: true,
            progress: {
              stage: progress.stage,
              message: progress.message,
              subMessage: progress.subMessage,
              completed: progress.completed,
              total: progress.total,
            },
          },
        });

        // 2. 점진적 로딩 상태 업데이트
        if (progress.stage && progress.stage !== 'initial') {
          dispatch({
            type: ThreadsTypes.UPDATE_LOADING_PROGRESS,
            payload: {
              stage: progress.stage,
              dateRange: progress.dateRange || { start: null, end: null },
              pageCount: progress.pageCount || 0,
              threadCount: progress.threadCount || 0,
              lastSavedDate: progress.lastSavedDate || null,
              isSaving: progress.stage === 'saving',
              progress: progress.progress || 0,
              error: progress.error || null
            }
          });
        }

        // 3. 페이지별 데이터 누적 (점진적 로딩의 핵심)
        if (progress.threads && progress.threads.length > 0) {
          dispatch({
            type: ThreadsTypes.APPEND_THREADS_PAGE,
            payload: {
              threads: progress.threads,
              stage: progress.stage,
              pageCount: progress.pageCount || 0,
              threadCount: progress.threadCount || 0,
              dateRange: progress.dateRange || { start: null, end: null }
            }
          });
        }

        // 4. 캐시된 데이터가 있으면 즉시 설정하고 TopPerformanceThreads 모델 생성
        if (
          progress.stage === "cached" &&
          progress.data &&
          progress.data.length > 0
        ) {
          dispatch({ type: ThreadsTypes.SET_THREADS, payload: progress.data });

          // TopPerformanceThreads 모델 즉시 생성
          const topPerformanceModel = createTopPerformanceThreadsModel(
            progress.data
          );
          dispatch({
            type: ThreadsTypes.SET_SECTION_DATA,
            payload: {
              section: "topPerformanceThreads",
              data: topPerformanceModel,
            },
          });

          console.log("✅ TopPerformanceThreads 모델 생성 완료 (캐시 데이터)");
        }

        // 5. 최종 데이터 설정
        if (progress.stage === "complete" && progress.data) {
          dispatch({ type: ThreadsTypes.SET_THREADS, payload: progress.data });

          // TopPerformanceThreads 모델 최종 업데이트
          const topPerformanceModel = createTopPerformanceThreadsModel(
            progress.data
          );
          dispatch({
            type: ThreadsTypes.SET_SECTION_DATA,
            payload: {
              section: "topPerformanceThreads",
              data: topPerformanceModel,
            },
          });

          // 점진적 로딩 완료 상태 업데이트
          dispatch({
            type: ThreadsTypes.UPDATE_LOADING_PROGRESS,
            payload: {
              stage: 'complete',
              progress: 100
            }
          });

          console.log("✅ TopPerformanceThreads 모델 최종 업데이트 완료");
        }
      },
      isAutoUpdate // 자동 업데이트인 경우 강제 업데이트
    );

    // Step 2: 인사이트 데이터 처리 + 관련 모델 생성
    dispatch({
      type: ThreadsTypes.SET_LOADING_STATE,
      payload: {
        progress: {
          stage: "insights",
          message: i18n.t("loading.processing_insights"),
          subMessage: i18n.t("loading.calculating_metrics"),
        },
      },
    });

    const [lifetimeInsight, weeklyEngagement] = await Promise.allSettled([
      // 인사이트 데이터
      fetchLifetimeInsightsPure().then((insight) => ({
        ...insight,
        numThreads: allThreads.length,
      })),
      // 주간 참여도 계산
      computeWeeklyEngagement(allThreads),
    ]);

    // 결과 처리 및 모델 생성
    if (lifetimeInsight.status === "fulfilled") {
      dispatch({
        type: ThreadsTypes.SET_INSIGHT,
        payload: lifetimeInsight.value,
      });

      // Firestore insight/insight 문서에 비차단 저장 (Admin 의존성 유지)
      try {
        await db.collection('user')
          .doc(userId)
          .collection('insight')
          .doc('insight')
          .set({ list: lifetimeInsight.value }, { merge: true });
      } catch (e) {
        console.warn('⚠️ Failed to persist insight (non-critical):', e);
      }

      // 2. PerformanceSummary 모델 생성
      const performanceSummaryModel = createPerformanceSummaryModel(
        lifetimeInsight.value,
        userProfile
      );
      dispatch({
        type: ThreadsTypes.SET_SECTION_DATA,
        payload: {
          section: "performanceSummary",
          data: performanceSummaryModel,
        },
      });

      console.log("✅ PerformanceSummary 모델 생성 완료");

      // 4. KeyMetricChart 모델 생성 (weeklyEngagement와 함께)
      if (weeklyEngagement.status === "fulfilled") {
        dispatch({
          type: ThreadsTypes.SET_WEEKLY_ENGAGEMENT,
          payload: weeklyEngagement.value,
        });

        const keyMetricChartModel = createKeyMetricChartModel(
          lifetimeInsight.value,
          weeklyEngagement.value
        );
        dispatch({
          type: ThreadsTypes.SET_SECTION_DATA,
          payload: { section: "keyMetricChart", data: keyMetricChartModel },
        });

        console.log("✅ KeyMetricChart 모델 생성 완료");
      } else {
        console.error(
          "❌ Weekly engagement calculation failed:",
          weeklyEngagement.reason
        );
        dispatch({ type: ThreadsTypes.SET_WEEKLY_ENGAGEMENT, payload: [] });
      }
    } else {
      console.error("❌ Insights fetch failed:", lifetimeInsight.reason);
    }

    // Step 3: 박스플롯 데이터 계산 + ThreadPerformanceOverview 모델 생성
    const boxPlotData = computeBoxPlotData(allThreads);
    dispatch({
      type: ThreadsTypes.SET_COMPUTED_DATA,
      payload: { boxPlotData },
    });

    // 3. ThreadPerformanceOverview 모델 생성
    const threadPerformanceModel = createThreadPerformanceOverviewModel(
      allThreads,
      boxPlotData
    );
    dispatch({
      type: ThreadsTypes.SET_SECTION_DATA,
      payload: {
        section: "threadPerformanceOverview",
        data: threadPerformanceModel,
      },
    });

    console.log("✅ ThreadPerformanceOverview 모델 생성 완료");

    // Step 4~5 (친구 상호작용 데이터 수집/분석)은 글로벌 로딩에서 제외
    // 해당 데이터는 FriendsInteraction 라우트 진입 시 수동으로 로딩함
    console.log("⏭️ Skipping friends interaction loading in global flow");

    // Step 7: 최근 스레드 인사이트 업데이트 (항상 1주 이내 갱신 시도)
    // 최초 가입 시에도 스레드가 존재하면 최신 지표 확인을 수행
    if (allThreads.length > 0) {
      try {
        console.log('🔄 Checking for recent threads to update insights...');
        
        dispatch({
          type: ThreadsTypes.SET_LOADING_STATE,
          payload: {
            progress: {
              stage: "updating-recent",
              message: i18n.t("loading.updating_recent_insights"),
              subMessage: i18n.t("loading.checking_recent_threads"),
            },
          },
        });

        const updateResult = await updateRecentThreadsData(userId, allThreads, dispatch);
        
        if (updateResult.count > 0) {
          console.log(`✅ Updated ${updateResult.count} recent threads with latest insights`);
          
          // 업데이트된 스레드가 있으면 allThreads 갱신
          allThreads = await fetchLifetimeThreadsOptimized(userId, null, false);
        }
      } catch (updateError) {
        console.warn('⚠️ Failed to update recent threads insights (non-critical):', updateError);
        // 업데이트 실패해도 계속 진행
      }
    } else {
      console.log('⏭️ Skipping recent insights update (no threads to update)');
    }

    // 모든 데이터 로딩 완료
    dispatch({ type: ThreadsTypes.SET_LIFETIME_READY, payload: true });

    // Step 8: 모든 데이터 분석 완료
    dispatch({
      type: ThreadsTypes.SET_LOADING_STATE,
      payload: {
        progress: {
          stage: "complete",
          message: i18n.t("loading.analysis_complete"),
          subMessage: i18n.t("loading.data_ready", {
            count: allThreads.length,
          }),
        },
      },
    });
  } catch (error) {
    console.error("💥 Critical error in lifetime data loading:", error);
    dispatch({ type: ThreadsTypes.SET_ERROR, payload: error.message });
  } finally {
    dispatch({ type: ThreadsTypes.SET_LIFETIME_LOADING, payload: false });
  }
}

/**
 * 친구 상호작용 아바타를 Firebase에서 보강 (백그라운드 비동기)
 * friendProfileCache(Firestore) 우선 조회 → 없으면 백엔드 fetchFriendProfiles 로 스크랩+Storage 저장.
 * 결과 storageUrl을 repliesByUser[].profileImage 로 병합해 섹션 모델을 재디스패치한다.
 * 실패해도 섹션은 ready 상태를 유지하며 이니셜 fallback으로 표시된다.
 *
 * @param {function} dispatch - ThreadsContext dispatch
 * @param {Array} userInteractions - analyzeUserInteractions 결과 (username, userId 포함)
 * @param {Array} conversations - lifetimeReplies (모델 재생성용)
 */
async function enrichFriendsInteractionAvatars(dispatch, userInteractions, conversations) {
  try {
    if (!Array.isArray(userInteractions) || userInteractions.length === 0) return;

    // 화면에 노출되는 상위 친구들만 대상으로 보강 (TopFriends 5 + AllFriends 20)
    const targets = userInteractions.slice(0, 25).filter((u) => u.username);
    if (targets.length === 0) return;

    const friendsForFetch = targets.map((u) => ({ username: u.username, userId: u.userId }));
    const pics = await fetchFriendProfilePictures(friendsForFetch);

    const pictureMap = {};
    pics.forEach((p) => {
      if (p.storageUrl) pictureMap[p.username] = p.storageUrl;
    });

    if (Object.keys(pictureMap).length === 0) return;

    const enriched = userInteractions.map((u) => ({
      ...u,
      profileImage: pictureMap[u.username] || u.profileImage || null,
    }));

    dispatch({
      type: ThreadsTypes.SET_COMPUTED_DATA,
      payload: { repliesByUser: enriched },
    });
    dispatch({
      type: ThreadsTypes.SET_SECTION_DATA,
      payload: {
        section: 'friendsInteraction',
        data: createFriendsInteractionModel(enriched, conversations),
      },
    });

    console.log(`✅ Enriched friends interaction avatars: ${Object.keys(pictureMap).length}/${targets.length}`);
  } catch (error) {
    console.warn('⚠️ Friend avatar enrich failed (non-critical):', error);
  }
}

/**
 * FriendsInteraction 섹션 전용 수동 로딩 함수
 * 글로벌 로딩과 분리되어 동작하며, isLifetimeLoading이나 글로벌 progress를 변경하지 않는다.
 *
 * @param {function} dispatch - ThreadsContext dispatch
 * @param {array} lifetimeThreadsList - 전체 스레드 목록
 * @param {object} options - 옵션 { year: number } - year가 있으면 해당 연도 전체, 없으면 최근 3개월
 */
export async function loadFriendsInteractionData(dispatch, lifetimeThreadsList, options = {}) {
  try {
    // 섹션 준비 상태 false로 설정
    dispatch({
      type: ThreadsTypes.SET_SECTION_DATA,
      payload: {
        section: 'friendsInteraction',
        data: { isReady: false, error: null },
      },
    });

    let targetThreads;

    if (options.year) {
      // 연말정산용: 해당 연도 전체 스레드
      console.log(`📅 Loading conversations for year ${options.year} (full year)`);
      targetThreads = (Array.isArray(lifetimeThreadsList) ? lifetimeThreadsList : []).filter((thread) => {
        const threadDate = moment(thread.timestamp);
        return threadDate.year() === options.year;
      });
    } else {
      // 기본: 최근 3개월 스레드만 대상으로 대화 데이터 로딩
      targetThreads = (Array.isArray(lifetimeThreadsList) ? lifetimeThreadsList : []).filter((thread) => {
        const threadDate = moment(thread.timestamp);
        const threeMonthsAgo = moment().subtract(3, 'months');
        return threadDate.isAfter(threeMonthsAgo);
      });
    }

    console.log(`🔄 Loading conversations for ${targetThreads.length} threads`);
    const conversations = await fetchAllConversationsPure(targetThreads, (progress) => {
      // 점진적 로딩 콜백 - 발견된 유저네임과 진행률 dispatch
      dispatch({
        type: ThreadsTypes.SET_FRIENDS_LOADING_PROGRESS,
        payload: {
          completed: progress.completed,
          total: progress.total,
          usernames: progress.usernames || [],
          conversations: progress.conversations || [],
        },
      });
    });
    console.log(`✅ Fetched ${conversations?.length || 0} conversation groups`);

    // 원본 대화 및 파생 데이터 설정
    dispatch({ type: ThreadsTypes.SET_LIFETIME_REPLIES, payload: conversations });

    const userInteractions = analyzeUserInteractions(conversations);
    console.log(`✅ Analyzed ${userInteractions?.length || 0} user interactions`);

    dispatch({
      type: ThreadsTypes.SET_COMPUTED_DATA,
      payload: { repliesByUser: userInteractions },
    });

    // 섹션 데이터 모델 완료 처리
    const friendsInteractionModel = createFriendsInteractionModel(
      userInteractions,
      conversations
    );
    console.log('✅ Created friendsInteractionModel, dispatching SET_SECTION_DATA...', {
      isReady: friendsInteractionModel.isReady,
      repliesByUserCount: friendsInteractionModel.repliesByUser?.length,
    });

    dispatch({
      type: ThreadsTypes.SET_SECTION_DATA,
      payload: { section: 'friendsInteraction', data: friendsInteractionModel },
    });

    console.log('✅ FriendsInteraction section data dispatched successfully');

    // Firebase 아바타 보강 (백그라운드, ready 상태 유지) - 캐시 우선, 없으면 백엔드 스크랩→Storage
    enrichFriendsInteractionAvatars(dispatch, userInteractions, conversations);

    return friendsInteractionModel;
  } catch (error) {
    console.error('❌ FriendsInteraction loading failed:', error);
    dispatch({
      type: ThreadsTypes.SET_SECTION_ERROR,
      payload: { section: 'friendsInteraction', error: error.message },
    });
    throw error;
  }
}

// === 섹션 데이터 모델 생성 함수들 export ===
export {
  createTopPerformanceThreadsModel,
  createPerformanceSummaryModel,
  createThreadPerformanceOverviewModel,
  createKeyMetricChartModel,
  createFriendsInteractionModel,
  createCollectionsModel,
};

// === 점진적 로딩 Custom Hooks ===

/**
 * 점진적 로딩 관리 훅
 */
export function useProgressiveLoading() {
  const state = useThreadsState();
  const dispatch = useThreadsDispatch();

  const progressiveState = state.progressiveLoading || {
    stage: 'initial',
    dateRange: { start: null, end: null },
    pageCount: 0,
    threadCount: 0,
    lastSavedDate: null,
    isSaving: false,
    progress: 0,
    error: null
  };

  // 스레드 페이지 추가 (페이징 단위로 누적)
  const appendThreadsPage = useCallback((threads, progressData) => {
    dispatch({
      type: ThreadsTypes.APPEND_THREADS_PAGE,
      payload: {
        threads,
        ...progressData
      }
    });
  }, [dispatch]);

  // 로딩 진행상황 업데이트
  const updateLoadingProgress = useCallback((progressData) => {
    dispatch({
      type: ThreadsTypes.UPDATE_LOADING_PROGRESS,
      payload: progressData
    });
  }, [dispatch]);

  // 표시 가능한 스레드 수 늘리기 (무한 스크롤용)
  const loadMoreVisibleThreads = useCallback((increment = 20) => {
    const newLimit = state.displayLimit + increment;
    dispatch({
      type: ThreadsTypes.UPDATE_VISIBLE_THREADS,
      payload: { limit: newLimit }
    });
  }, [dispatch, state.displayLimit]);

  // 로딩 상태 초기화
  const resetLoadingState = useCallback(() => {
    dispatch({
      type: ThreadsTypes.RESET_LOADING_STATE
    });
  }, [dispatch]);

  return {
    // 상태
    progressiveState,
    visibleThreads: state.visibleThreads || [],
    displayLimit: state.displayLimit || 20,
    isLoading: state.isLifetimeLoading,
    
    // 액션
    appendThreadsPage,
    updateLoadingProgress,
    loadMoreVisibleThreads,
    resetLoadingState
  };
}

// === 최근 스레드 인사이트 업데이트 함수 ===

/**
 * 1주일 이내의 스레드 인사이트 업데이트
 * @param {string} userId - 사용자 ID
 * @param {Array} lifetimeThreads - 전체 스레드 리스트
 * @param {Function} dispatch - Context dispatch 함수
 * @returns {Promise<Object>} 업데이트 결과
 */
export async function updateRecentThreadsData(userId, lifetimeThreads, dispatch) {
  console.log('🔄 Starting recent threads insights update...');
  
  try {
    // 1. 최근 스레드 인사이트 업데이트
    const updateResult = await updateRecentThreadsInsights(lifetimeThreads);
    
    if (updateResult.count === 0) {
      console.log('📊 No recent threads to update');
      return updateResult;
    }
    
    // 2. Firestore에 업데이트된 데이터 저장
    await updateThreadsInFirestore(userId, updateResult.updatedThreads);
    
    // 3. Context 상태 업데이트
    const updatedThreadsMap = new Map(
      updateResult.updatedThreads.map(thread => [thread.id, thread])
    );
    
    // 기존 스레드 리스트 업데이트
    const updatedLifetimeThreads = lifetimeThreads.map(thread => {
      if (updatedThreadsMap.has(thread.id)) {
        return updatedThreadsMap.get(thread.id);
      }
      return thread;
    });
    
    // Context에 업데이트된 스레드 반영
    dispatch({ 
      type: ThreadsTypes.SET_THREADS, 
      payload: updatedLifetimeThreads 
    });
    
    // 섹션별 모델도 업데이트
    // 1. TopPerformanceThreads 모델 업데이트
    const topPerformanceModel = createTopPerformanceThreadsModel(
      updatedLifetimeThreads
    );
    dispatch({
      type: ThreadsTypes.SET_SECTION_DATA,
      payload: {
        section: "topPerformanceThreads",
        data: topPerformanceModel,
      },
    });
    
    // 3. ThreadPerformanceOverview 모델 업데이트
    const boxPlotData = computeBoxPlotData(updatedLifetimeThreads);
    const threadPerformanceModel = createThreadPerformanceOverviewModel(
      updatedLifetimeThreads,
      boxPlotData
    );
    dispatch({
      type: ThreadsTypes.SET_SECTION_DATA,
      payload: {
        section: "threadPerformanceOverview",
        data: threadPerformanceModel,
      },
    });
    
    console.log(`✅ Successfully updated ${updateResult.count} recent threads`);
    return updateResult;
    
  } catch (error) {
    console.error('❌ Error updating recent threads:', error);
    throw error;
  }
}

// === Timeline Custom Hooks ===

/**
 * Timeline 액션 훅 - Dashboard에서 사용
 */
export function useTimelineActions() {
  const dispatch = useThreadsDispatch();
  const state = useThreadsState();

  // Phase 설정
  const setTimelinePhase = (phase) => {
    dispatch({ type: ThreadsTypes.SET_TIMELINE_PHASE, payload: phase });
  };

  // Lifetime 데이터 설정
  const setLifetimeData = (data) => {
    dispatch({ type: ThreadsTypes.SET_LIFETIME_DATA, payload: data });
  };

  // 월별 데이터 추가
  const addTimelineMonth = (monthData, source = "api") => {
    dispatch({
      type: ThreadsTypes.ADD_TIMELINE_MONTH,
      payload: { monthData, source },
    });
  };

  // 타임라인 플래그 업데이트
  const updateTimelineFlags = (flags) => {
    dispatch({ type: ThreadsTypes.UPDATE_TIMELINE_FLAGS, payload: flags });
  };

  // 로딩 상태 설정
  const setTimelineLoading = (isLoading) => {
    dispatch({ type: ThreadsTypes.SET_TIMELINE_LOADING, payload: isLoading });
  };

  // API에서 다음 월 로드
  const loadNextMonthFromAPI = async () => {
    const { timeline } = state;
    
    console.log(`🔄 [loadNextMonthFromAPI] 시작`, {
      hasMoreFromAPI: timeline.hasMoreFromAPI,
      isLoadingMore: timeline.isLoadingMore,
      currentMonthsCount: timeline.months?.length || 0
    });

    if (!timeline.hasMoreFromAPI || timeline.isLoadingMore) {
      console.log(`⚠️ [loadNextMonthFromAPI] 조건 불만족:`, {
        hasMoreFromAPI: timeline.hasMoreFromAPI,
        isLoadingMore: timeline.isLoadingMore
      });
      return false;
    }

    setTimelineLoading(true);

    try {
      // 현재 로드된 가장 오래된 월을 찾음
      const currentMonths = timeline.months
        .filter((m) => m.source === "api")
        .sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          return a.month - b.month;
        });

      let targetMonth, targetYear;

      if (currentMonths.length === 0) {
        // 첫 로드: 현재 월
        const now = moment();
        targetMonth = now.month() + 1;
        targetYear = now.year();
      } else {
        // 이전 월 계산 (더 오래된 데이터)
        const oldest = currentMonths[0];
        const prevDate = moment(`${oldest.year}-${oldest.month}-01`).subtract(
          1,
          "month"
        );
        targetMonth = prevDate.month() + 1;
        targetYear = prevDate.year();
      }

      // API에서 월별 데이터 가져오기
      console.log(`📡 [loadNextMonthFromAPI] API 호출: ${targetYear}-${targetMonth}`);
      const monthData = await fetchMonthlyData(targetMonth, targetYear);
      console.log(`📊 [loadNextMonthFromAPI] API 응답:`, {
        hasThreads: !!monthData.threads,
        threadsWeekCount: monthData.threads?.week?.length || 0,
        hasInsight: !!monthData.insight
      });

      // 데이터 구조 확인 및 보정
      const processedData = {
        month: targetMonth,
        year: targetYear,
        threads: monthData.threads || {
          month: targetMonth,
          year: targetYear,
          week: [],
        },
        insight: monthData.insight || {
          month: targetMonth,
          year: targetYear,
          views: 0,
          likes: 0,
          replies: 0,
          reposts: 0,
          profileViews: [],
        },
      };

      console.log(`✅ [loadNextMonthFromAPI] Timeline에 추가: ${targetYear}-${targetMonth}`);
      addTimelineMonth(processedData, "api");

      // 더 이상 로드할 데이터가 있는지 확인 - 실제로는 사용자 계정 생성일이나 서비스 시작일 기준
      // 현재는 넉넉하게 2년 전까지 허용
      const serviceStartDate = moment().subtract(2, 'years');
      const currentDate = moment(`${targetYear}-${targetMonth}-01`);

      if (currentDate.isBefore(serviceStartDate)) {
        console.log(`📅 [loadNextMonthFromAPI] 서비스 시작일(${serviceStartDate.format('YYYY-MM')}) 이전이므로 hasMoreFromAPI = false`);
        updateTimelineFlags({ hasMoreFromAPI: false });
      }

      return true;
    } catch (error) {
      console.error("Error loading next month from API:", error);
      // Preserve structured auth/recovery errors for Dashboard. Returning
      // false here used to turn OAuth code 190 into a generic empty result.
      throw error;
    } finally {
      setTimelineLoading(false);
    }
  };

  // Lifetime에서 다음 월 추출
  const loadNextMonthFromLifetime = () => {
    const { timeline } = state;

    if (!timeline.hasMoreFromLifetime || !state.isLifetimeReady) {
      return false;
    }

    try {
      // 현재 로드된 월들 중 lifetime에서 가져오지 않은 가장 오래된 월 찾기
      const apiMonths = timeline.months.filter((m) => m.source === "api");

      if (apiMonths.length === 0) {
        return false;
      }

      const oldestApiMonth = apiMonths.sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      })[0];

      const prevDate = moment(
        `${oldestApiMonth.year}-${oldestApiMonth.month}-01`
      ).subtract(1, "month");
      const targetMonth = prevDate.month() + 1;
      const targetYear = prevDate.year();

      // Lifetime 데이터에서 해당 월 추출
      const monthData = extractMonthlyDataFromLifetime(
        state.lifetimeThreadsList,
        targetMonth,
        targetYear
      );

      if (monthData) {
        addTimelineMonth(monthData, "lifetime");
        return true;
      } else {
        updateTimelineFlags({ hasMoreFromLifetime: false });
        return false;
      }
    } catch (error) {
      console.error("Error loading next month from lifetime:", error);
      return false;
    }
  };

  return {
    setTimelinePhase,
    setLifetimeData,
    addTimelineMonth,
    updateTimelineFlags,
    setTimelineLoading,
    loadNextMonthFromAPI,
    loadNextMonthFromLifetime,
  };
}

/**
 * Dashboard 전용 타임라인 훅
 */
export function useDashboardTimeline() {
  const state = useThreadsState();
  const actions = useTimelineActions();

  const { timeline } = state;

  // 현재 단계별 상태
  const isInitialPhase = timeline.phase === "initial";
  const isAPILoadingPhase = timeline.phase === "api-loading";
  const isLifetimeReadyPhase = timeline.phase === "lifetime-ready";
  const isCompletePhase = timeline.phase === "complete";

  // 데이터 사용 가능 여부
  const hasTimelineData = timeline.months.length > 0;
  const hasLifetimeData = state.isLifetimeReady; // lifetime 객체 대신 직접 상태 사용
  const canUseLifetimeCache = isLifetimeReadyPhase || isCompletePhase;

  // 무한 스크롤을 위한 로드 함수
  const loadMore = async () => {
    if (timeline.isLoadingMore) {
      return false;
    }

    // 항상 API를 우선으로 사용 (lifetime 로직 제거)
    if (timeline.hasMoreFromAPI) {
      return await actions.loadNextMonthFromAPI();
    }

    return false;
  };

  // 초기 데이터 로드
  const initializeTimeline = async () => {
    if (timeline.phase !== "initial") {
      return;
    }

    actions.setTimelinePhase("api-loading");

    try {
      // 첫 번째 월 (현재 월) 로드
      const success = await actions.loadNextMonthFromAPI();
      if (!success) {
        throw new Error("Failed to load initial month data");
      }
    } catch (error) {
      console.error("Timeline initialization failed:", error);
      // Allow an explicit retry after a transient failure or recovered session.
      actions.setTimelinePhase("initial");
      throw error;
    }
  };

  // Lifetime 완료 시 phase 전환
  const handleLifetimeComplete = () => {
    if (hasLifetimeData && timeline.phase === "api-loading") {
      actions.setTimelinePhase("lifetime-ready");
    }
  };

  return {
    // 상태
    timeline,
    isInitialPhase,
    isAPILoadingPhase,
    isLifetimeReadyPhase,
    isCompletePhase,
    hasTimelineData,
    hasLifetimeData,
    canUseLifetimeCache,

    // 액션
    loadMore,
    initializeTimeline,
    handleLifetimeComplete,
    ...actions,
  };
}
